import { CvssVersion } from '../cvss-enums';

interface CvssVersionCheckResult {
    version: CvssVersion;
    matchedPrefix: string;
}

function checkCVSSVersion(vector: string): CvssVersionCheckResult {
    const versionCheck = /^CVSS:(?<major>\d)\.(?<minor>\d)\//.exec(vector);
    const { major, minor } = versionCheck?.groups ?? {};

    if (versionCheck) {
        const matchedPrefix = versionCheck[0];
        switch (`${major}.${minor}`) {
            case '2.0':
                return { version: CvssVersion.CVSS20, matchedPrefix };
            case '3.0':
                return { version: CvssVersion.CVSS30, matchedPrefix };
            case '3.1':
                return { version: CvssVersion.CVSS31, matchedPrefix };
            case '4.0':
                return { version: CvssVersion.CVSS40, matchedPrefix };
        }
    }

    if (vector.startsWith('CVSS')) {
        throw new RangeError(
            `Bad version prefix. Supported versions are: 'CVSS:2.0/', 'CVSS:3.0/', 'CVSS:3.1/', 'CVSS:4.0/'.`,
        );
    }

    const unwrappedVector =
        vector.startsWith('(') && vector.endsWith(')')
            ? vector.slice(1, -1)
            : vector;
    if (unwrappedVector.startsWith('CVSS')) {
        throw new RangeError(
            `Bad version prefix. Supported versions are: 'CVSS:2.0/', 'CVSS:3.0/', 'CVSS:3.1/', 'CVSS:4.0/'.`,
        );
    }
    const metrics = unwrappedVector
        .split('/')
        .map((segment) => segment.split(':')[0]);

    if (
        metrics.includes('PR') ||
        metrics.includes('UI') ||
        metrics.includes('S')
    ) {
        return { version: CvssVersion.CVSS31, matchedPrefix: '' };
    }

    return { version: CvssVersion.CVSS20, matchedPrefix: '' };
}

export function getCvssVersion(vector: string): CvssVersion {
    return checkCVSSVersion(vector).version;
}

export function stripCvssVersionPrefix(vector: string): string {
    const { matchedPrefix } = checkCVSSVersion(vector);
    const strippedVector = vector.replace(matchedPrefix, '');
    return strippedVector.startsWith('(') && strippedVector.endsWith(')')
        ? strippedVector.slice(1, -1)
        : strippedVector;
}

/**
 * Rounds the floating point value up to its nearest multiple of 0.1 (i.e. to one decimal place).
 *
 * Behavior specified in: https://www.first.org/cvss/v3.1/specification-document#Appendix-A---Floating-Point-Rounding
 *
 * @param value - the value to round
 * @returns the rounded value
 */
export function roundUp(value: number): number {
    const rounded = Math.round(value * 100000);
    return rounded % 10000 === 0
        ? rounded / 100000.0
        : (Math.floor(rounded / 10000) + 1) / 10.0;
}

export function roundToFirstDecimal(value: number): number {
    return Math.round(value * 10) / 10;
}
